/**
 * APIs used in License Transactions:
 * - GET /orgs/:orgId/license/history.read (or /ents/:entId/orgs/:orgId/license/history.read)
 */

import React, { useState, useEffect, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { cloneDeep } from "lodash";
import dayjs from "dayjs";

// material-ui
import { useTheme } from "@mui/material/styles";
import { Box, Grid, Typography, Divider, Stack } from "@mui/material";
import { FormattedMessage } from "react-intl";

// project import
import TableField from "components/Table";
import SearchInput from "components/SearchInput";
import CircularLoader from "components/CircularLoader";

// assets
import { ReactComponent as ExportIcon } from "assets/images/icons/icn_btn_export.svg";
import icon_refresh from "assets/images/icons/icn_refresh.svg";

// hooks
import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";

// utils
import { exportTableDataToCSV } from "utils/export";

// local import
import { defaultTabs, getTransactionsTableColumns } from "./const";
import TransactionTypeDropdown from "./components/TransactionTypeDropdown";

const normalizeTransactionType = (type) => {
  if (!type) return "";
  const t = type.toLowerCase();
  if (t === "import" || t === "deposit") return "import";
  if (t === "subscribe") return "subscribe";
  if (t === "monthlycharge") return "monthlyCharge";
  if (t === "adddevice") return "addDevice";
  if (t === "removedevice") return "removeDevice";
  if (t === "unsubscribe") return "unsubscribe";
  return type;
};

const Transactions = () => {
  const theme = useTheme();
  const { auth, standalone, isAuthReady } = useAuthScope();
  const { sendRequest, getData, dataMap } = useDataSourceManager();

  const [isLoading, setIsLoading] = useState(true);
  const [historyData, setHistoryData] = useState([]);

  // Initialize with all types selected
  const initialTypes = useMemo(
    () => defaultTabs.filter((t) => t.value !== "all").map((t) => t.value),
    [],
  );
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [tabCounts, setTabCounts] = useState({});

  const historyUuid = useMemo(() => uuidv4(), []);

  const fetchLicenseHistory = () => {
    if (!isAuthReady || (!auth.orgs?.id && !auth.realm?.id)) return;
    setIsLoading(true);

    const realmId = auth.realm?.id || "";
    const orgId = auth.orgs?.id || "";
    const historyKey = standalone
      ? "/orgs/:orgId/license/history.read"
      : "/ents/:entId/orgs/:orgId/license/history.read";
    const restfulParams = standalone
      ? { orgId: realmId }
      : { entId: realmId, orgId };

    const now = dayjs().unix();
    const oneYearAgo = dayjs().subtract(1, "year").unix();

    sendRequest(historyKey, historyUuid, restfulParams, {
      filter: {
        from: oneYearAgo,
        to: now,
      },
    });
  };

  useEffect(() => {
    fetchLicenseHistory();
  }, [isAuthReady, auth.orgs?.id, auth.realm?.id]);

  useEffect(() => {
    const response = getData(historyUuid);
    if (response?.data) {
      const normalizedData = response.data.map((tx) => {
        const normalizedType = normalizeTransactionType(tx.type);
        const typeKey = normalizedType?.toLowerCase() || "";
        let description = tx.description || "";

        if (tx.descriptionParams && typeof tx.descriptionParams === "object") {
          if (typeKey === "import") {
            description = `${tx.descriptionParams.account || "-"} | ${tx.descriptionParams.key || "-"}`;
          } else {
            description = Object.entries(tx.descriptionParams)
              .map(([k, v]) => `${k}: ${v}`)
              .join(" | ");
          }
        }

        return { ...tx, type: normalizedType, description };
      });

      setHistoryData(normalizedData);

      const counts = { all: normalizedData.length };
      normalizedData.forEach((tx) => {
        counts[tx.type] = (counts[tx.type] || 0) + 1;
      });
      setTabCounts(counts);
      setIsLoading(false);

      if (selectedTypes.length === 0) {
        setSelectedTypes(initialTypes);
      }
    }
  }, [dataMap, historyUuid, initialTypes]);

  const filteredData = useMemo(() => {
    if (selectedTypes.length === initialTypes.length) return historyData;
    return historyData.filter((tx) => selectedTypes.includes(tx.type));
  }, [selectedTypes, historyData, initialTypes]);

  const dropdownOptions = useMemo(() => {
    return defaultTabs
      .filter((tab) => tab.value !== "all")
      .map((tab) => ({
        ...tab,
        tabCount: tabCounts[tab.value] || 0,
      }));
  }, [tabCounts]);
  console.log(historyData);
  const tableInstance = useTableInstance({
    tableName: "TransactionsTable",
    data: filteredData,
    columns: getTransactionsTableColumns({ theme }),
    enableGlobalFilter: true,
    globalFilterMode: "column",
    globalFilterColumns: ["formattedDate", "type", "description"],
    enableSorting: true,
    enablePagination: true,
  });

  const isEmptyTable = historyData.length === 0 && !isLoading;

  return (
    <Box>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{
          mb: 3,
          height: "64px",
          mt: -1, // Adjust to align with top of sidebar
        }}
      >
        <Typography
          variant='h4'
          sx={{
            color: "#172664",
            fontFamily: theme.typography.fontFamily,
            fontSize: "20px",
            fontWeight: "bold",
          }}
        >
          <FormattedMessage id='transactions' />
        </Typography>
      </Stack>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        <Grid
          container
          alignItems='center'
          justifyContent='space-between'
          sx={{ mb: 3 }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              sx={{
                mr: 2,
                fontSize: "14px",
                fontWeight: 600,
                color: "#111",
              }}
            >
              Show
            </Typography>
            <TransactionTypeDropdown
              selectedTypes={selectedTypes}
              onChange={setSelectedTypes}
              options={dropdownOptions}
              totalCount={tabCounts.all || 0}
            />
          </Box>

          <Stack direction='row' alignItems='center' spacing={2}>
            <Divider
              sx={{ width: "1px", height: "40px" }}
              orientation='vertical'
              flexItem
            />
            <SearchInput
              value={tableInstance?.globalFilter}
              onChange={tableInstance?.setGlobalFilter}
              placeholder='Search by Date, Type'
              sx={{ width: "280px" }}
            />
            <Box
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                p: 1,
                borderRadius: "4px",
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
              }}
              onClick={fetchLicenseHistory}
            >
              <img src={icon_refresh} alt='refresh' />
            </Box>
          </Stack>
        </Grid>

        <Box sx={{ overflow: "hidden" }}>
          <TableField
            {...tableInstance}
            pendingChildren={
              isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularLoader />
                </Box>
              ) : historyData.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography color='textSecondary'>
                    No data available
                  </Typography>
                </Box>
              ) : null
            }
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Transactions;
